var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"a35b0940102a23510053e5a912657960e28df4a9"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

/**
 * This file configures the initialization of Sentry on the browser. The config
 * you add here will be used whenever a page is visited.
 * https://docs.sentry.io/platforms/javascript/guides/nextjs/
 *
 * After migrate to Next.JS v13, implement `thirdPartyErrorFilterIntegration`
 * Sentry config to improve filtering of 3p errors:
 * https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
 */
import { extraErrorDataIntegration } from '@sentry/integrations';
import { init } from '@sentry/nextjs';

const INTERNAL_GOOGLE_ADS_DIRECTORY = 'pageFold';

/**
 * @type {import('@sentry/nextjs').BrowserOptions}
 */
init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  debug: false,
  integrations: [
    // TODO: check if this integration is the cause to various console errors
    // started being captured by Sentry in final of October
    // captureConsoleIntegration(),
    extraErrorDataIntegration(),
  ],
  // send only 20% of transactions
  tracesSampleRate: 0.2,
  // send only 50% of captured errors
  sampleRate: 0.5,
  // Reference:
  // https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
  ignoreErrors: [
    // References:
    // - https://github.com/getsentry/sentry-javascript/issues/3440
    // - https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
    'Non-Error promise rejection captured',
    /**
     * Common error for blocked third party resources by extensions like uBO.
     * Network errors that are outside of our control.
     * Examples:
     * - https://promobit-organization.sentry.io/issues/5938873434/?project=5581922&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&statsPeriod=14d&stream_index=8
     * - https://promobit-organization.sentry.io/issues/5705614745/?project=5581922&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&statsPeriod=14d&stream_index=7
     * - https://promobit-organization.sentry.io/issues/5786924055/?project=5581922&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&statsPeriod=14d&stream_index=17
     * - https://promobit-organization.sentry.io/issues/2704001953/ (156k+ events - Load failed on Safari/iOS)
     */
    'Failed to fetch',
    'Load failed',
    /TypeError: Load failed/i,
    // Random plugins/extensions
    'top.GLOBALS',
    // See: https://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See https://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See https://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Avast extension error
    '_avast_submit',
    // Temporarily ignore Firefox bug with Fetch API https://bugzilla.mozilla.org/show_bug.cgi?id=1280189
    'TypeError: NetworkError when attempting to fetch resource.',
    // Temporarily ignore NextJS router error with trailingSlash config https://github.com/vercel/next.js/issues/39638#issuecomment-1221104901
    /Invariant: attempted to hard navigate to the same URL/i,
    // This error to be resolved after Safari implements the WebPush API, what should be happen in your next versions https://webkit.org/blog/12945/meet-web-push/
    /This browser doesn't support the API's required to use the Firebase SDK/i,
    // Sentry report error that shouldn't be logged in production
    'Failed to load resource: the server responded with a status of 429 (Too Many Requests)',
    // Caused by AdBlock extensions that uses "Better Js Popunder Script"
    /Cannot redefine property: BetterJsPop/i,
    /require is not defined/i,
    // Caused by browsers extensions, like:
    // - Grammarly
    // - Katalon Recorder
    /Identifier 'originalPrompt' has already been declared/i,
    /Error response received for message <get-frame-manager-configuration>/i,
    /jwplayer is not defined/i,
    /Unexpected token '<'/i,
    /Unexpected token '>'/i,
    /GH is not defined/i,
    // Common Google Ads errors
    /\(tag\/js\/gpt\)/i,
    /gpt\/pubads/i,
    /null is not an object (evaluating 'CleverCore.DOM.getByID(v).style')/i,
    /ResizeObserver loop completed with undelivered notifications./i,
    // Competing company extension issue
    "Failed to execute 'define' on 'CustomElementRegistry': the name \"cuponomia-store-bar\" has already been used with this registry",
    // Metamask's extension issue
    'Object captured as promise rejection with keys: code, message',
    // Apple Sign In popup closed by user (expected user behavior, not an error)
    // Reference: https://promobit-organization.sentry.io/issues/5561199990/
    'popup_closed_by_user',
    // status codes
    /429/i,
    /401/i,
    // Errors related to Google Ads
    /\[GPT\]/i,
    "Cannot read properties of null (reading 'addService')",
    'NS_ERROR_NOT_INITIALIZED',
    /FBNav/i,
    "Cannot destructure property 'address' of '(intermediate value)' as it is undefined.",
    /SYNC\.JS:/i,
    // Crawler errors
    /bis_frame_id/i,
    /Script called 1 times/i,
    /Pop-up candidates final number:/i,
    /Pop-up candidates final number :/i,
    /fSCsetInTicket/i,
    /bis_log_from_js@/i,
    /found unique frames/i,
    // Error caused by some Google Ads
    "TypeError: Cannot read properties of null (reading 'content')",
    // Temporarily disable during we search for the origin of these errors
    /a is null/i,
    /null has no properties/i,
    /'get' on proxy: property 'javaEnabled' is a read-only and non-configurable/i,
    'Attempting to use a disconnected port object',
    /Can't find variable: gn/i,
    /Failed to connect to MetaMask/i,
    /signal is aborted without reason/i,
    /signal is aborted/i,
    'Invalid left-hand side expression in prefix operation',
    "Cannot read properties of undefined (reading 'ton')",
    "Cannot read properties of undefined (reading 'domInteractive')",
    "Identifier 'h' has already been declared",
    "Can't find variable: webkit",
    'Talisman extension has not been configured yet. Please continue with onboarding.',
    'Error invoking enableButtonsClickedMetaDataLogging: Java object is gone',
    "Failed to execute 'get' on 'CredentialsContainer': Failed to read the 'identity' property from 'CredentialRequestOptions': Failed to read the 'providers' property from 'IdentityCredentialRequestOptions': Required member is undefined.",
    'r.ethereum.setExternalProvider is not a function',
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
    'Failed to initialize messaging: tx_attempts_exceeded {"diagnostics":{"errors":["Error: tx_ack_timeout","Error: tx_ack_timeout","Error: tx_ack_timeout","Error: tx_ack_timeout","Error: tx_ack_timeout","Error: tx_ack_timeout","Error: tx_ack_timeout","Er...',
    'window.ethereum._handleChainChanged is not a function',
    "Cannot read properties of null (reading 'document')",
    "Failed to read the 'localStorage' property from 'Window': Access is denied for this document.",
    'Attempting to use a disconnected port object',
    'Talisman extension has not been configured yet. Please continue with onboarding.',
    'AbortError: The user aborted a request.',
    /Maximum call stack size exceeded/i,
    /out of memory/i,
    "Unexpected identifier 'https'",
    "Failed to read a named property 'document' from 'Window': Blocked a frame with origin \"https://www.promobit.com.br\" from accessing a cross-origin frame.",
    /Invalid call to runtime.sendMessage(). Tab not found./,
    /The operation is insecure./,
  ],
  denyUrls: [
    // Google Adsense
    /pagead\/js/i,
    // Common urls denied by adblockers
    /securepubads\.g\.doubleclick\.net/i,
    /pagead2\.googlesyndication\.com/i,
    /tag\/js\/gpt/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    // Browser extension content scripts
    /app:\/\/\/js\/content\.js/i,
    /content\.js$/i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /ingest\.sentry\.io/i,
    /http:\/\/lopmenedo.com/i,
  ],
  beforeSend: (event) => {
    /**
     * There's a issue on Sentry that it apparently being forced by a specific
     * user and IP. This is issue is no-op and is heavily contributing for the
     * degradation of our quota. We filter these for now.
     */
    if (
      event?.user?.username === 'anderson_barros7451' ||
      event?.user?.ip_address === '131.72.222.141'
    ) {
      return null;
    }

    /**
     * The Google Ads has several internal issues, one of these was the
     * un-control of errors of ads, making Sentry capturing several noisy
     * errors that are invisible to final user.
     *
     * Before Sentry send the error and your stack-trace to server we attempt to
     * verify if this error is originated from a Google Ad. This validation
     * isn't 100% assertive but it possibly reduces the amount of noisy errors
     * processed by Sentry.
     */
    for (let i in event.exception?.values?.[0]?.stacktrace?.frames) {
      if (
        event.exception?.values?.[0]?.stacktrace?.frames[i]?.filename.includes(
          INTERNAL_GOOGLE_ADS_DIRECTORY
        )
      )
        return null;
    }

    /**
     * Filter errors from ftUtils.js (third-party ad library).
     * These errors occur during visibilitychange events when the page is being
     * unloaded or hidden, causing null reference errors that are not
     * actionable. Reference: https://promobit-organization.sentry.io/issues/6566621310/
     */
    const stackFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const isFromFtUtils = stackFrames.some((frame) =>
      frame.filename?.includes('ftUtils.js')
    );

    if (isFromFtUtils) {
      return null;
    }

    /**
     * Filter SyntaxError on /promocoes/recentes/ page.
     * This error occurs in compiled/bundled code (line 1) and has been
     * happening since 2023, likely caused by third-party scripts or bundling
     * edge cases that are not actionable. Reference:
     * https://promobit-organization.sentry.io/issues/4280746789/
     */
    const errorMessage = event.exception?.values?.[0]?.value || '';
    const errorType = event.exception?.values?.[0]?.type || '';
    const requestUrl = event.request?.url || '';
    const firstFrame = stackFrames[0];

    if (
      errorType === 'SyntaxError' &&
      errorMessage.includes("Unexpected token 'else'") &&
      requestUrl.includes('/promocoes/recentes/') &&
      firstFrame?.lineno === 1
    ) {
      return null;
    }

    /**
     * Filter showModal() errors related to HTMLDialogElement.
     * These errors occur due to browser compatibility issues (older browsers
     * don't support showModal) or when trying to open an already-open dialog.
     * We handle these gracefully in the code, but filter as a safety net.
     * References:
     * - https://promobit-organization.sentry.io/issues/7060723940/
     * - https://promobit-organization.sentry.io/issues/6997315527/
     * - https://promobit-organization.sentry.io/issues/6943827025/
     */
    if (
      (errorType === 'TypeError' &&
        errorMessage.includes('showModal is not a function')) ||
      (errorType === 'InvalidStateError' &&
        errorMessage.includes(
          "Failed to execute 'showModal' on 'HTMLDialogElement'"
        ))
    ) {
      return null;
    }

    return event;
  },
});
